import React, { createContext, useContext } from 'react';
import useGameGraphics from '../hooks/useGameGraphics';

const GraphicsContext = createContext();

export const useGraphics = () => {
  const context = useContext(GraphicsContext);
  if (!context) {
    throw new Error('useGraphics must be used within GraphicsProvider');
  }
  return context;
};

export const GraphicsProvider = ({ children }) => {
  // Use the existing graphics hook
  const {
    destructibleWallImage,
    indestructibleWallImage,
    floorImage,
    bombImage,
    bombSVGText,
    fireCenterImage,
    fireLineImage,
    fireEndImage,
    characterSprites,
    characterSpriteSVGs,
    svgSpritesLoaded,
    coloredSpritesCache,
    coloredBombCache,
    createColoredSprite,
    createColoredBomb
  } = useGameGraphics();

  // Graphics utilities
  const utilities = React.useMemo(() => ({
    createColoredSprite,
    createColoredBomb,
    
    // Image getters
    getDestructibleWallImage: () => destructibleWallImage,
    getIndestructibleWallImage: () => indestructibleWallImage,
    getFloorImage: () => floorImage,
    
    getBombImage: () => bombImage,
    
    getFireImage: (type = 'center') => {
      switch (type) {
        case 'center': return fireCenterImage;
        case 'line': return fireLineImage;
        case 'end': return fireEndImage;
        default: return fireCenterImage;
      }
    },
    
    getCharacterSprite: (playerId) => {
      return characterSprites.get(playerId) || characterSprites.values().next().value;
    },
    
    getColoredSprite: (playerId, color) => {
      const cacheKey = `${playerId}_${color}`;
      return coloredSpritesCache.get(cacheKey);
    },
    
    // Sprite utilities
    isSpritesLoaded: () => svgSpritesLoaded,
    hasCharacterSprite: (playerId) => characterSprites.has(playerId),
    
    // Cache utilities
    clearSpriteCache: () => {
      coloredSpritesCache.clear();
      coloredBombCache.clear();
    },
    
    getCacheSize: () => ({
      sprites: coloredSpritesCache.size,
      bombs: coloredBombCache.size
    })
  }), [
    destructibleWallImage,
    indestructibleWallImage,
    floorImage,
    bombImage,
    fireCenterImage,
    fireLineImage,
    fireEndImage,
    characterSprites,
    svgSpritesLoaded,
    coloredSpritesCache,
    coloredBombCache,
    createColoredSprite,
    createColoredBomb
  ]);

  // Memoized state values
  const state = React.useMemo(() => ({
    destructibleWallImage,
    indestructibleWallImage,
    floorImage,
    bombImage,
    bombSVGText,
    fireCenterImage,
    fireLineImage,
    fireEndImage,
    characterSprites,
    characterSpriteSVGs,
    svgSpritesLoaded,
    coloredSpritesCache,
    coloredBombCache
  }), [
    destructibleWallImage,
    indestructibleWallImage,
    floorImage,
    bombImage,
    bombSVGText,
    fireCenterImage,
    fireLineImage,
    fireEndImage,
    characterSprites,
    characterSpriteSVGs,
    svgSpritesLoaded,
    coloredSpritesCache,
    coloredBombCache
  ]);

  const value = React.useMemo(() => ({
    ...state,
    ...utilities
  }), [state, utilities]);

  return (
    <GraphicsContext.Provider value={value}>
      {children}
    </GraphicsContext.Provider>
  );
};

export default GraphicsContext;